import React, { useEffect, useRef, useState, useContext, useMemo } from 'react';
import { getLocale, useIntl, useSelector, shallowEqual } from 'umi';
import moment from 'moment';
import { Select, TableColumn } from 'tea-component';
import { SortBy } from 'tea-component/es/table/addons';
import HeatMapTable, { creatHeatColumns, HeatCellDataStruct, HeatMapTableColumn } from '@/pages/dashboard/components/HeatMapTable';
import Accumulate from '@/pages/dashboard/components/Accumulate';
import MetricAndUpDown from '@/pages/dashboard/components/MetricAndUpDown';
import { formatNum, formatPercentNum, getUpAndDownStatus, toUpperFirstCase, getDynamicLocalCnEnText, formatFilterValues, getDateWeekDayStr, formatAccumulation, getChartDateFormatString } from '@/pages/dashboard/utils/utils';
import { KEY_DAYS_DEFAULT_VALUE } from '@/pages/dashboard/pages/monetization/_config/constant';
import { useSelectArrObj } from '@/pages/dashboard/utils/customHook';
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from "@/pages/dashboard/components/DownloadFormatModal";
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import { useData, useLtvTrackingCoMemo } from './hooks';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { downLoadApi } from '@/pages/dashboard/services/common';
import { LANGUAGE_MAP, PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import './index.scss';
import { getMultiSortParams } from '@/pages/dashboard/components/OverallTableCo/utils';
import { WIDTH_MAP_RETURN_TABLE } from '@/pages/dashboard/pages/engagement/retention/components/RetentionReturnTableCo/_constant';
import { getDateFormatRangeString, getDateFormatString } from '@/pages/dashboard/components/OverallTableCo/hooks';
import { IConfigData } from '@/pages/dashboard/components/PageContainer/type';

/**
 * 传入参数
 */
interface Props {
  config: any; // 配置内容
  data: IConfigData;
  style: string;
  className: string;
  /** 当为 'aggs' 时请求会带上 logical_flag: 'weighted_daily_average' */
  segmentVal?: string;
}

/**
 *  表格请求参数
 */
interface TableReqParams {
  game_code: string; // 游戏码,根据游戏吗取对应游戏的数据
  start_time: string; // 开始时间,参数示例:2022-01-02
  end_time: string; // 结束时间,参数示例:2022-01-02
  filters: {
    zone_list: string[]; // 游戏区服,多选,参数示例:[1,2,3]
    market_list: string[]; // 地区类型,参数示例:region
    os_list: string[]; // 玩家操作平台,多选,参数示例:[1,2,3]
    channel_list: string[]; // 渠道标识,多选,参数示例:[3]
  };

  // sorted_by: string // 排序指标名称
  desc: boolean; // 【选填】是否降序列
  order_by: string[]; // 【选填】可以排序,register_cnt
  cycle: string[]; // 日期统计维度 //day,week,month
  tag: string; // 必填】哪种数据 //active: 活跃用户, new：新用户
  column_len: number; // 【选填】 最近几个周期
  // len: number // 【选填】 最近几个周期
  group: string[]; // 【必填】分组统计//country,channel,region,zone
  page_size: number; // 每页数量，参数示例：10
  page: number; // 页码，参数示例：1
}

interface IKeyDaysConfigItem {
  value: number;
  enName?: string;
  cnName?: string;
  /** 连续区间是否包含 0D LT；未配置时可由文案「0 ~ N」自动推断 */
  includeLt0?: boolean;
  includedDay0?: boolean;
}

/** 下拉文案是否为「0 ~ N 日/Days」区间（隐含从 0D 开始） */
const isZeroStartRangeLabel = (option?: {
  enName?: string;
  cnName?: string;
}): boolean => {
  if (!option) return false;
  const pattern = /^0\s*[~～\-–—]\s*\d+/;
  return pattern.test(option.enName || '') || pattern.test(option.cnName || '');
};

export default function HeatTable(props: Props) {
  const { config, style, className, segmentVal } = props;
  const {
    title,
    titleCN,
    componentMetrics: propsComponentMetrics,
    keyDaysConfig,
    api: { tableDownloadApi },
    tableDisWidthMap = WIDTH_MAP_RETURN_TABLE,
    ltvType = 'LTV',
    showPaymentAmount = true,
    bubbleClassName,
    accumulateSelectMultipleBoxClassName,
    dateOptionDisabled: configDateOptionDisabled,
    keyDayColumns: configKeyDayColumns,
    /** 连续区间模式是否包含 0D（0d_ltv）；可被 keyDaysConfig 单项 includeLt0 / includedDay0 覆盖 */
    includeLt0: configIncludeLt0,
    includedDay0: configIncludedDay0,
  } = config;
  const globalIncludeLt0 = configIncludeLt0 ?? configIncludedDay0 ?? false;
  const { gameCode, selector } = useSelector((state: any) => {
    return {
      gameCode: state?.dashboard?.currentGame?.game_code,
      selector: state?.dashboardSetting?.selector ?? {}
    };
  }, shallowEqual);
  const contextObj = useContext(FilterContext);
  const { filterLoading, filterVal = {} } = contextObj || {};
  const isEn = getLocale() === 'en-US';
  const { formatMessage } = useIntl();
  const intl = useIntl();
  // 获取 messages 对象
  const messages = (intl as any)?.messages || {};
  // Accumulated 筛选器（accumulated 为已提交维度；draft 仅用于下拉勾选，关闭后再同步宽表）
  const [accumulated, setAccumulated] = useState<string[]>(['date']);
  const [accumulatedDraft, setAccumulatedDraft] = useState<string[]>(['date']);
  const hasDate = accumulated?.includes('date');
  // Key Days 筛选器
  const [keyDays, setKeyDays] = useState(KEY_DAYS_DEFAULT_VALUE);

  const [columns, setColumns] = useState<HeatMapTableColumn[]>([{ name: "", features: { sortable: true } }]); // 默认值是为了解决控制台warning, 无功能作用

  const accumulateOldValueRef = useRef(['date']);
  // 宽表数据
  const [tableData, setTableData] = useState<TableColumn[]>([]);
  const [tableCopyData, setTableCopyData] = useState<TableColumn[]>([]);
  const dateOptionDisabled = configDateOptionDisabled ?? true;
  let selectOptions = useSelectArrObj(filterVal);
  selectOptions = selectOptions?.filter((i: { groupKey: string; }) => i?.groupKey === 'separated');
  selectOptions.unshift({
    groupKey: 'separated',
    value: 'date',
    text: formatMessage({ id: 'common_by_Date' }),
    disabled: dateOptionDisabled,
  } as any);

  // 排序值
  const [sortValue, setSortValue] = useState<SortBy[]>([]);

  // 表格分页记录
  const [tablePaging, setTablePaging] = useState({ pageIndex: 1, pageSize: 10 });
  const [total, setTotal] = useState(0);

  const extraParams = useMemo(() => {
    if (segmentVal === 'aggs') {
      return { logical_flag: 'weighted_daily_average' };
    }
    return undefined;
  }, [segmentVal]);

  const ltvTableData = useData(config, filterVal, filterLoading, accumulated, extraParams);
  const {
    getLtvTrackingFunc,
    componentData,
    error,
    loading,
    overLimitBarTriggerKey,
  } = ltvTableData;

  const {
    componentMetrics,
  } = useLtvTrackingCoMemo({
    data: props?.data,
    propsComponentMetrics
  });

  const tableTitle = useMemo(() => {
    return isEn ? title : titleCN;
  }, [isEn]);
  const { setNoticeStatus } = useNotificationOfDownloadTable(tableTitle);
  const timeType = filterVal?.timeType || 'Day';

  const formatRowDateStr = (
    date: string | undefined,
    timeTypeKey: 'Day' | 'Week' | 'Month',
  ): string => {
    if (!date) return '-';
    const dateValue = String(date).trim();
    if (dateValue.includes('~')) {
      return getDateFormatRangeString(timeTypeKey, dateValue);
    }
    return getDateFormatString(timeTypeKey, dateValue) || dateValue;
  };

  const dateColumnWidth = hasDate ? 160 : 220;
  const DATE_COLUMN = {
    code: 'date',
    width: dateColumnWidth,
    align: 'left',
    lock: true,
    features: { sortable: true },
    name: formatMessage({ id: 'monetization_ltv_Date' }),
    render: (record: any) => {
      const dateVal = typeof record === 'object' ? record?.date : record;
      if (!dateVal) return <span>-</span>;
      if (dateVal?.includes?.('~')) {
        const dateStr = getDateFormatRangeString(timeType, String(dateVal).trim());
        return <span>{dateStr}</span>;
      }
      const displayDate = getChartDateFormatString(timeType, dateVal, true) || dateVal;
      const { weekStr, isSunOrSat } = getDateWeekDayStr(dateVal, formatMessage);
      const isMonth = timeType === 'Month';
      return <>
        <span>{displayDate}</span>
        {!isMonth && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekStr}</span>}
      </>;
    }
  };

  const getNormalColumns = () => {
    // register列配置
    const metricsConfig = componentMetrics?.[0];
    const { enName, cnName } = metricsConfig || {};
    const name = isEn ? enName : cnName;
    const REGISTER_COLUMN = {
      code: 'register_users',
      width: 200,
      title: name,
      align: 'right',
      selectType: 'dod',
      features: { sortable: true },
      render: ({ value, dod, status }) => {
        return (
          <MetricAndUpDown
            metric={value}
            updown={{ data: dod, status }}
            hasTooltip={false}
          />
        );
      },
    };


    // accumulate列配置（排除 date，支持取消 By Date 后按其他维度展示）
    const accumulateColumns: { code: string; width: number; align: string; name: string; features: { sortable: boolean; }; }[] = [];
    accumulated.forEach((val) => {
      if (val === 'date') return;
      const option = selectOptions.find((opt: { value: string; }) => opt.value === val);
      let hasTranslation = false;
      const messageId = `common_${toUpperFirstCase(option?.value)}`;
      if(option){
        // 检查 key 是否存在
        hasTranslation = Object.prototype.hasOwnProperty.call(messages, messageId);
      }
      const name = hasTranslation ? formatMessage({ id: messageId }) : formatMessage({ id: `common_${option?.value}` }) || option?.text || '';
      option && accumulateColumns.push({
        code: val,
        // width: ['region']?.includes(val) ? 246 : 146,
        width: tableDisWidthMap?.[val as keyof typeof tableDisWidthMap] || 146,
        align: 'left',
        name,
        features: { sortable: true }
      });
    });
    return [DATE_COLUMN, ...accumulateColumns, REGISTER_COLUMN];
  };

  const DEFAULT_KEY_DAY_COLUMNS = [1, 2, 3, 7, 14, 30, 60, 90, 180, 270, 360];

  const keyDayList = useMemo(
    () =>
      configKeyDayColumns?.length
        ? configKeyDayColumns
        : DEFAULT_KEY_DAY_COLUMNS,
    [configKeyDayColumns],
  );

  /** 当前下拉是否展示 0D LT（0d_ltv）列 */
  const resolveIncludeLt0 = (selectedKeyDays: number): boolean => {
    if (selectedKeyDays === 0) {
      return keyDayList.includes(0);
    }
    const selectedOption = keyDaysConfig?.find(
      (item: IKeyDaysConfigItem) => item?.value === selectedKeyDays,
    ) as IKeyDaysConfigItem | undefined;
    const explicitFlag =
      selectedOption?.includeLt0 ?? selectedOption?.includedDay0;
    if (typeof explicitFlag === 'boolean') {
      return explicitFlag;
    }
    if (isZeroStartRangeLabel(selectedOption)) {
      return true;
    }
    return globalIncludeLt0;
  };

  const buildHeatDayColumn = (day: number) => ({
    code: `${day}d_ltv`,
    title: `${day}${formatMessage({ id: 'monetization_ltv_D' })} ${ltvType}`,
    width: day === 0 ? 110 : 125,
  });

  /** 浮窗指标名与列头一致，按 ltvType 区分 LT / LTV */
  const formatHeatMetricBubbleTitle = (metricKey: string): string => {
    const dayMatch = metricKey.match(/^(\d+)d_ltv$/i);
    if (dayMatch) {
      return `${dayMatch[1]}${formatMessage({ id: 'monetization_ltv_D' })} ${ltvType}`;
    }
    const legacyMatch = metricKey.match(/^d_(\d+)_ltv$/i);
    if (legacyMatch) {
      return `${legacyMatch[1]}${formatMessage({ id: 'monetization_ltv_D' })} ${ltvType}`;
    }
    let title = metricKey.replace(/_/g, ' ').toUpperCase();
    title = title.replace(/\bLTV\b/g, ltvType);
    if (getLocale() === 'zh-CN') {
      title = title.replace(/\bD\b/g, '天');
    }
    return title;
  };

  // 根据接口获取热力图列
  const getHeatColumns = (selectedKeyDays = keyDays) => {
    const includeLt0 = resolveIncludeLt0(selectedKeyDays);
    const columns =
      selectedKeyDays === 0
        ? keyDayList.map((num: number) => ({
          ...buildHeatDayColumn(num),
          width: 130,
        }))
        : createContinuityHeatColumns(selectedKeyDays, includeLt0);

    return creatHeatColumns(columns, bubbleClassName);
  };

  function createContinuityHeatColumns(
    days: number,
    includeLt0: boolean,
  ) {
    const heatColumns: HeatMapTableColumn[] = [];
    const startDay = includeLt0 ? 0 : 1;
    for (let i = startDay;i <= days;i++) {
      heatColumns.push(buildHeatDayColumn(i));
    }
    return heatColumns;
  }

  useEffect(() => {
    setColumns([...getNormalColumns(), ...getHeatColumns(keyDays)]);
  }, [
    keyDays,
    accumulated,
    isEn,
    keyDayList,
    globalIncludeLt0,
    keyDaysConfig,
    ltvType,
    timeType,
    formatMessage,
  ]);

  useEffect(() => {
    const { metrics_list = [], average = {}, row_count } = componentData ?? {};
    const tableData = handleTableData(metrics_list,false) ?? [];
    const tableCopyData = handleTableData(metrics_list,true) ?? [];
    // 判断是否为最后一页展示Avg行 // 采用前端分页器计算, 是因为后端返回的最后一页条数是实际条数, 不是每页条数
    const { pageIndex: page, pageSize: page_size } = tablePaging;
    if (page * page_size >= Number(row_count)) {
      tableData.push(handleAvgData(average));
      tableCopyData.push(handleAvgData(average));
    }
    setTableData(tableData);
    setTableCopyData(tableCopyData);
    setTotal(row_count || 0);
  }, [componentData, getLocale(), keyDays, accumulated, timeType]);

  useEffect(() => {
    setTablePaging({ pageIndex: 1, pageSize: 10 });
    setKeyDays(KEY_DAYS_DEFAULT_VALUE);
    setSortValue([]);
  }, [filterVal]);

  /**
   * 获取上传参数对象
   */
  const getTableReqParams = (paging: React.SetStateAction<{ pageIndex: number; pageSize: number; }>, sort: React.SetStateAction<SortBy<any>[]> | { code: string; order: string; }[]): TableReqParams => {
    setTablePaging(paging);
    setSortValue(sort);

    const { rangeTime, timeType: filterTimeType, ...filters } = filterVal || {};
    const { code, order } = sort?.[0] || { code: 'date', order: 'desc' };
    const accumulation = formatAccumulation(accumulated);
    const params = {
      game_code: gameCode,
      start_time: moment(rangeTime?.[0]).format('YYYY-MM-DD'),
      end_time: moment(rangeTime?.[1]).format('YYYY-MM-DD'),
      period: PERIODTIMEOBJ?.[filterTimeType] || 'daily',
      filters: formatFilterValues(filters),
      key_days: keyDays,
      accumulation,
      sorted_by: code,
      desc: order === 'desc',
      page: paging.pageIndex,
      page_size: paging.pageSize,
      multi_sort: getMultiSortParams([], [], { by: code, order }, 'dod', true),
    };
    return { ...params, ...(extraParams || {}) };
  };

  /** 返回数据格式整理 */
  const handleTableData = (data: any[] = [],isCopy: boolean) => {
    const timeTypeKey = (filterVal?.timeType || 'Day') as 'Day' | 'Week' | 'Month';
    return data?.map(({ date, metrics = {}, ...accumulateValue } = {}) => {
      const dateStr = formatRowDateStr(date, timeTypeKey);
      const { register_users, ...otherMetrics } = metrics || {};
      // 计算 LTV 数据
      const ltvs: HeatCellDataStruct = {};
      const metricKeys = Object.keys(otherMetrics);
      metricKeys?.forEach((key) => {
        const metricTitle = formatHeatMetricBubbleTitle(key);
        let [ltv = '', income = ''] = metrics[key]?.value?.split(':') || [];
        ltv = isCopy ? ltv:  formatNum(ltv);
        income = isCopy ? income:  formatNum(income);
        ltvs[key] = {
          bubble: {
            content: (<>
              <span className='table-bubble-date'>{dateStr}</span>
              {showPaymentAmount &&
                <div className='table-bubble-info'>
                  <span className="Revenue-table-Lspan">{formatMessage({ id: 'monetization_revenue_Revenue($)' },{unit: getMonetUnit()})}：</span>
                  <span className='Revenue-table-Rspan'>{income}</span>
                </div>
              }
              <div className='table-bubble-info'>
                <span className="Revenue-table-Lspan">{`${metricTitle}`}：</span>
                <span className='Revenue-table-Rspan'>{ltv}</span>
              </div>
            </>)
          },
          cellContent: ltv,
          colorPercent: heatCellColorPercent(ltv),
        };
      });

      // 计算 Accumulate筛选器的动态列的数据
      const accumulatedData = {};
      accumulated?.filter(a => a !== 'date')?.map((value) => {
        accumulatedData[value] = getDynamicLocalCnEnText(value, accumulateValue[value], gameCode, getLocale(), selector) || "-";
      });
      return {
        date: dateStr,
        ...accumulatedData,
        register_users: {
          value: isCopy ? register_users?.value : formatNum(register_users?.value, false, true),
          dod: isCopy ? register_users?.dod : formatPercentNum(parseFloat(register_users?.dod), 1, true),
          status: isCopy ? register_users?.dod : getUpAndDownStatus(register_users?.dod)
        },
        ...ltvs,
      };
    });
  };



  // avg 数据格式整理
  const handleAvgData = (avgData = {}) => {
    const { date, metrics = {}, ...accumulateValue } = avgData ?? {};
    const avgDateLabel = ['平均值', 'Average'].includes(date)
      ? formatMessage({ id: 'common_Average' })
      : date;
    // 计算 Accumulate筛选器的动态列的数据
    const accumulatedData = {};
    accumulated?.filter(a => a !== 'date')?.map((value) => {
      accumulatedData[value] = getDynamicLocalCnEnText(value, accumulateValue[value], gameCode, getLocale(), selector) || "-";
    });

    // 计算avg行, ltv单元格的数据
    const avgLtvs = {};
    const { register_users, ...allMetrics } = metrics;
    const metricKeys = Object.keys(allMetrics);
    metricKeys?.forEach((key) => {
      const splitVal = metrics[key]?.value?.split(':') || [];
      let ltv: string = splitVal[0] ?? '';
      const income: string = splitVal[1] ?? '';
      ltv = formatNum(ltv);
      const metricTitle = formatHeatMetricBubbleTitle(key);
      avgLtvs[key] = {
        bubble: {
          content: (<>
            <span className='table-bubble-date'>{avgDateLabel}</span>
            {showPaymentAmount &&
              <div className='table-bubble-info'>
                <span className="Revenue-table-Lspan">{formatMessage({ id: 'monetization_ltv_Revenue($)' },{unit: getMonetUnit()})}：</span>
                <span className='Revenue-table-Rspan'>{formatNum(income)}</span>
              </div>
            }
            <div className='table-bubble-info'>
              <span className="Revenue-table-Lspan">{`${metricTitle}`}：</span>
              <span className='Revenue-table-Rspan'>{ltv}</span>
            </div>
          </>)
        },
        cellContent: ltv,
        colorPercent: heatCellColorPercent(ltv),
      };
    });

    return {
      date: avgDateLabel,
      ...accumulatedData,
      register_users: {
        value: formatNum(register_users?.value, false, true),
        status: "no",
        dod: ""
      },
      ...avgLtvs
    };
  };

  // 计算热力图显示颜色的程度, 对照规则如下
  // [0-0.50）    0%
  // [0.50-1.00）  5%
  // [1.00-1.50） 10%
  // [1.50-2.00） 20%
  // [2.00-2.50)  30%
  // [2.50,3.00）  40%
  // [3.00,3.50）  50%
  // [3.50,4.00）  60%
  // [4.00,4.50）   70%
  // [4.50,5.00）   80%
  // [5.00,∞）      85%
  const heatCellColorPercent = (ltv: string | string[]) => {
    // 当 ltv字符串中包含 逗号时, 说明数值超出一千, 需要返回 0.85
    const floatLtv = ltv?.includes(",") ? 5 : parseFloat(ltv);
    if (isNaN(floatLtv) || (floatLtv >= 0 && floatLtv < 0.5)) {
      return 0;
    } if (floatLtv >= 0.5 && floatLtv < 1) {
      return 0.05;
    } if (floatLtv >= 1 && floatLtv < 5) {
      return (Math.trunc((floatLtv / 0.5) - 1)) * 0.1;
    }
    return 0.85;

  };

  /**
   * 下载文件
   */
  function download() {
    const downloadPromise = downPromise?.();
    if (downloadPromise != null) { // 有返回Promise对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus("loading");
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${tableTitle.replace(/\u0020/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus("success");
            },
            onDownloadError() {
              setNoticeStatus("error");
            }
          });
        } catch (e) {
          setNoticeStatus("error");
        }
      })();
    }
  }

  function downPromise () {
    const params: TableReqParams = getTableReqParams(tablePaging, sortValue);
    const language = LANGUAGE_MAP[getLocale()];
    const { beforeDownloadRequest } = config;
    return downLoadApi?.(`${tableDownloadApi}?language=${language}`, beforeDownloadRequest ? beforeDownloadRequest(params) : params);
  }

  const KEY_DAYS_OPTIONS = useMemo(() => {
    return keyDaysConfig?.map((item: { value: any; enName: any; cnName: any; }) => {
      return {
        value: item?.value,
        text: isEn ? item?.enName : item?.cnName
      };
    });
  }, [isEn, keyDaysConfig]);

  return (
    <div className={`dbd-monetization-ltv-table-co ${className}`} style={style}>
      <HeatMapTable
        title={tableTitle}
        copyData={tableCopyData}
        records={tableData}
        defaultColumnWidth={50}
        columns={columns}
        timeType={timeType}
        sorting={{
          sorts: sortValue,
          onChangeSorts: (nextSorts) => {
            setSortValue(nextSorts);
            getLtvTrackingFunc(keyDays, tablePaging, nextSorts);
          },
        }}
        paging={{
          pageSize: tablePaging.pageSize,
          pageIndex: tablePaging.pageIndex,
          recordCount: total,
          onPagingChange: (pagingQuery) => {
            setTablePaging(pagingQuery);
            getLtvTrackingFunc(keyDays, pagingQuery, sortValue);
          },
          pageSizeOptions: [10, 20, 30, 50],
        }}
        loading={filterLoading||loading}
        error={error}
        onDownload={download}
        frontLogicalFlag={componentData?.front_logical_flag}
        limitCount={config?.tableConfig?.overLimitCount}
        barLimitCount={config?.tableConfig?.overLimitBarCount}
        barDownloadDisabled={config?.tableConfig?.barDownloadDisabled}
        overLimitBarTriggerKey={overLimitBarTriggerKey}
        topTip={
          {
            left: (<>
              <Accumulate
                accumulateSelectMultipleBoxClassName={accumulateSelectMultipleBoxClassName}
                className='register-table-select'
                value={accumulatedDraft}
                selectOptions={selectOptions}
                onOpenChange={(open) => {
                  if (open) {
                    setAccumulatedDraft([...accumulated]);
                  }
                }}
                onChange={(value) => {
                  setAccumulatedDraft(value);
                }}
                onClose={() => {
                  if (accumulateOldValueRef?.current?.toString() === accumulatedDraft?.toString()) {
                    return;
                  }
                  accumulateOldValueRef.current = accumulatedDraft;
                  setAccumulated(accumulatedDraft);
                  // 分页和排序重置
                  setTablePaging({ pageIndex: 1, pageSize: 10 });
                  setSortValue([]);
                  getLtvTrackingFunc(
                    keyDays,
                    { pageIndex: 1, pageSize: 10 },
                    [],
                    accumulatedDraft,
                  );
                }}
              />

              <Select
                className='register-table-select'
                boxClassName='register-table-select-box'
                appearance="button"
                matchButtonWidth
                listHeight={200}
                style={{ width: 150 }}
                options={KEY_DAYS_OPTIONS}
                value={keyDays}
                onChange={(value) => {
                  setKeyDays(value);
                  getLtvTrackingFunc(value, tablePaging, sortValue);
                }}
                placeholder={`Key Day ${ltvType}`}
              />
            </>)
          }
        }
      />
    </div>
  );
}
